/**
 * AiAssistInput.tsx
 *
 * A single-row AI instruction input (not a chat).
 * Used in PlanificationStep (and other Build-bar phases) to let the user
 * send a free-text modification request to the backend AI.
 *
 * States:
 *  - idle    — Input + "Enviar" button, both interactive.
 *  - loading — Input disabled, button shows spinner text.
 *  - error   — Red error message below the row; auto-dismisses after 8 s via
 *              `onClearError`. The input retains the user's text.
 *
 * The component is deliberately stateless about the instruction text after
 * a successful submit: the caller's `onSubmit` promise resolves → we clear
 * the local input. On rejection (error shown by parent), the text is kept.
 */

import { useState, useEffect, useRef, type KeyboardEvent } from 'react';

import { Spinner } from '@/components/ui';
import styles from './AiAssistInput.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AiAssistInputProps {
  /**
   * Placeholder text shown inside the text field when it is empty.
   * Example: "Pide a la IA que modifique el plan..."
   */
  placeholder: string;

  /**
   * Called when the user submits (Enter key or "Enviar" button).
   * The promise should resolve on success and reject / throw on failure.
   * The component clears the input text only after a successful resolve.
   */
  onSubmit: (instruction: string) => Promise<void>;

  /** When true the input is disabled and the button shows a loading state. */
  isLoading: boolean;

  /**
   * Current error string to display, or `null` when there is no error.
   * Passed down from the parent (or Zustand store) so errors survive re-renders.
   */
  error: string | null;

  /**
   * Called after the 8-second auto-dismiss timer fires, or immediately when
   * the user starts typing again after an error.
   * The parent should set its error state to `null` when this fires.
   */
  onClearError: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * AiAssistInput — reusable single-instruction AI input row.
 *
 * Does NOT maintain conversation history; each submission is independent.
 * The error auto-dismiss timer is scoped to this component via a ref so it
 * gets cleaned up when the component unmounts.
 */
export const AiAssistInput = ({
  placeholder,
  onSubmit,
  isLoading,
  error,
  onClearError,
}: AiAssistInputProps) => {
  // ── Local state ────────────────────────────────────────────────────────────

  /** The current text the user has typed. */
  const [text, setText] = useState('');

  // ── Error auto-dismiss timer ───────────────────────────────────────────────

  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Whenever a new error appears, start an 8-second auto-dismiss timer.
   * Cancel any previous timer first to avoid double-firing.
   */
  useEffect(() => {
    if (!error) return;

    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);

    dismissTimerRef.current = setTimeout(() => {
      onClearError();
    }, 8_000);

    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, [error, onClearError]);

  /** Clean up on unmount to prevent state updates on dead components. */
  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────

  /**
   * Submit the current instruction text.
   * Clears the input on success; keeps it on failure (so the user can retry).
   */
  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    try {
      await onSubmit(trimmed);
      // Success — clear input only after the promise resolves without error.
      setText('');
    } catch {
      // Error is surfaced via the `error` prop from the parent.
      // The input text is intentionally retained so the user can amend it.
    }
  };

  /**
   * Allow submitting with Enter (but not Shift+Enter, which would normally
   * insert a newline in a textarea — here we use a plain <input> so only
   * Enter matters).
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  /**
   * When the user starts typing after an error, clear the error immediately
   * so it does not mislead them about a past failed request.
   */
  const handleChange = (value: string) => {
    setText(value);
    if (error) {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      onClearError();
    }
  };

  // ── Derived state ──────────────────────────────────────────────────────────

  const canSubmit = text.trim().length > 0 && !isLoading;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={styles.wrapper}>
      {/* Input row */}
      <div className={styles.row}>
        <input
          type="text"
          className={styles.input}
          placeholder={placeholder}
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          aria-label={placeholder}
          aria-invalid={!!error}
          aria-describedby={error ? 'ai-assist-error' : undefined}
        />

        <button
          type="button"
          className={`${styles.submitButton} ${isLoading ? styles.submitButtonLoading : ''}`}
          onClick={() => void handleSubmit()}
          disabled={!canSubmit}
          aria-busy={isLoading}
          aria-label={isLoading ? 'Sending instruction...' : 'Send instruction to AI'}
        >
          {isLoading ? (
            <span className={styles.loadingContent}>
              <Spinner size="sm" />
              <span>Processing...</span>
            </span>
          ) : (
            'Send'
          )}
        </button>
      </div>

      {/* Error message — visible only when `error` is non-null */}
      {error && (
        <p
          id="ai-assist-error"
          className={styles.errorText}
          role="alert"
          aria-live="assertive"
        >
          {error}
        </p>
      )}
    </div>
  );
};
